/**
 * Promotion Countdown Timer
 * Displays countdown timer in Netherlands timezone with format: DD:HH:MM:SS
 */
class PromotionCountdownTimer {
  constructor() {
    this.timers = [];
    this.intervalId = null;
    this.targetTimezone = 'Europe/Amsterdam'; // Netherlands timezone
    this.init();
  }

  init() {
    // Find all countdown timer elements
    const timerElements = document.querySelectorAll('[data-countdown-timer]');
    
    if (timerElements.length === 0) return;

    // Initialize each timer
    timerElements.forEach((element, index) => {
      this.setupTimer(element, index);
    });

    // Start the countdown update interval
    this.startCountdown();
  }

  setupTimer(element, index) {
    // Get target date from data attribute or use default (end of current day)
    const targetDateStr = element.dataset.countdownTarget;
    let targetDate;

    if (targetDateStr) {
      // Parse the date string and treat it as Netherlands time
      // Use the same approach as the empty case but with custom date/time
      
      // Parse the input components
      const [datePart, timePart] = targetDateStr.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      
      // Create a date in Netherlands timezone using the same method as the empty case
      const now = new Date();
      const netherlandsNow = new Date(now.toLocaleString("en-US", {timeZone: this.targetTimezone}));
      
      // Set the specific date and time (same method as empty case)
      targetDate = new Date(netherlandsNow);
      targetDate.setFullYear(year, month - 1, day);
      targetDate.setHours(hours, minutes, seconds, 0);
    } else {
      // Default: end of current day (23:59:59) in Netherlands timezone
      const now = new Date();
      const netherlandsNow = new Date(now.toLocaleString("en-US", {timeZone: this.targetTimezone}));
      
      // Set to end of current day (23:59:59)
      targetDate = new Date(netherlandsNow);
      targetDate.setHours(23, 59, 59, 999);
    }

    // Create timer structure
    const timerHtml = `
      <div class="countdown-timer-display">
        <div class="countdown-unit">
          <span class="countdown-number days">00</span>
          <span class="countdown-label">D</span>
        </div>
        <div class="countdown-separator">|</div>
        <div class="countdown-unit">
          <span class="countdown-number hours">00</span>
          <span class="countdown-label">U</span>
        </div>
        <div class="countdown-separator">|</div>
        <div class="countdown-unit">
          <span class="countdown-number minutes">00</span>
          <span class="countdown-label">M</span>
        </div>
        <div class="countdown-separator">|</div>
        <div class="countdown-unit">
          <span class="countdown-number seconds">00</span>
          <span class="countdown-label">S</span>
        </div>
      </div>
    `;

    element.innerHTML = timerHtml;

    // Store timer configuration
    this.timers.push({
      element: element,
      targetDate: targetDate,
      daysEl: element.querySelector('.days'),
      hoursEl: element.querySelector('.hours'),
      minutesEl: element.querySelector('.minutes'),
      secondsEl: element.querySelector('.seconds')
    });
  }

  getCurrentNetherlandsTime() {
    // Get current time in Netherlands timezone
    const now = new Date();
    return new Date(now.toLocaleString("en-US", {timeZone: this.targetTimezone}));
  }

  formatNumber(num) {
    // Ensure numbers are always two digits (07 instead of 7)
    return num.toString().padStart(2, '0');
  }

  calculateTimeRemaining(targetDate) {
    const now = this.getCurrentNetherlandsTime();
    const timeDiff = targetDate.getTime() - now.getTime();

    if (timeDiff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        expired: true
      };
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      expired: false
    };
  }

  updateTimers() {
    this.timers.forEach(timer => {
      const timeRemaining = this.calculateTimeRemaining(timer.targetDate);

      if (timeRemaining.expired) {
        // Timer expired - show zeros and add expired class
        timer.daysEl.textContent = '00';
        timer.hoursEl.textContent = '00';
        timer.minutesEl.textContent = '00';
        timer.secondsEl.textContent = '00';
        timer.element.classList.add('countdown-expired');
      } else {
        // Update timer display
        timer.daysEl.textContent = this.formatNumber(timeRemaining.days);
        timer.hoursEl.textContent = this.formatNumber(timeRemaining.hours);
        timer.minutesEl.textContent = this.formatNumber(timeRemaining.minutes);
        timer.secondsEl.textContent = this.formatNumber(timeRemaining.seconds);
        timer.element.classList.remove('countdown-expired');
      }
    });
  }

  startCountdown() {
    // Update immediately
    this.updateTimers();

    // Update every second
    this.intervalId = setInterval(() => {
      this.updateTimers();
    }, 1000);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Method to add a new target date to existing timer
  setTargetDate(timerIndex, targetDateStr) {
    if (this.timers[timerIndex]) {
      this.timers[timerIndex].targetDate = new Date(targetDateStr + ' Europe/Amsterdam');
    }
  }

  // Method to set target date for all timers
  setTargetDateForAll(targetDateStr) {
    const targetDate = new Date(targetDateStr + ' Europe/Amsterdam');
    this.timers.forEach(timer => {
      timer.targetDate = targetDate;
    });
  }
}

// Auto-initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  window.promotionCountdownTimer = new PromotionCountdownTimer();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    window.promotionCountdownTimer = new PromotionCountdownTimer();
  });
} else {
  window.promotionCountdownTimer = new PromotionCountdownTimer();
}